import React from "react";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ClockIcon,
  XCircleIcon,
  SparklesIcon,
  DocumentTextIcon,
  EyeIcon,
  PencilIcon,
  ChartBarIcon,
  CurrencyPoundIcon,
  CalendarIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  BanknotesIcon,
} from "@heroicons/react/24/outline";
import {
  CheckCircleIcon as CheckCircleIconSolid,
  ExclamationCircleIcon as ExclamationCircleIconSolid,
  ExclamationTriangleIcon as ExclamationTriangleIconSolid,
  InformationCircleIcon as InformationCircleIconSolid,
  ClockIcon as ClockIconSolid,
} from "@heroicons/react/24/solid";

/**
 * StatusBadge Component
 *
 * Displays status badges with appropriate colors and icons
 */
export function StatusBadge({
  status,
  variant = "default", // "default", "solid", "outline", "minimal"
  size = "sm", // "xs", "sm", "md", "lg"
  showIcon = true,
  className = "",
}) {
  const statusConfig = {
    // Project/Deal/Valuation statuses
    active: {
      label: "Active",
      icon: CheckCircleIcon,
      solidIcon: CheckCircleIconSolid,
      colors: {
        default: "bg-green-100 text-green-800 border-green-200",
        solid: "bg-green-600 text-white",
        outline: "border-green-600 text-green-600",
        minimal: "text-green-600",
      },
    },
    // Valuation-specific statuses
    approved: {
      label: "Approved",
      icon: CheckCircleIcon,
      solidIcon: CheckCircleIconSolid,
      colors: {
        default: "bg-green-100 text-green-800 border-green-200",
        solid: "bg-green-600 text-white",
        outline: "border-green-600 text-green-600",
        minimal: "text-green-600",
      },
    },
    "under-review": {
      label: "Under Review",
      icon: ClockIcon,
      solidIcon: ClockIconSolid,
      colors: {
        default: "bg-yellow-100 text-yellow-800 border-yellow-200",
        solid: "bg-yellow-600 text-white",
        outline: "border-yellow-600 text-yellow-600",
        minimal: "text-yellow-600",
      },
    },
    draft: {
      label: "Draft",
      icon: PencilIcon,
      solidIcon: PencilIcon,
      colors: {
        default: "bg-gray-100 text-gray-800 border-gray-200",
        solid: "bg-gray-600 text-white",
        outline: "border-gray-600 text-gray-600",
        minimal: "text-gray-600",
      },
    },
    submitted: {
      label: "Submitted",
      icon: DocumentTextIcon,
      solidIcon: DocumentTextIcon,
      colors: {
        default: "bg-blue-100 text-blue-800 border-blue-200",
        solid: "bg-blue-600 text-white",
        outline: "border-blue-600 text-blue-600",
        minimal: "text-blue-600",
      },
    },
    rejected: {
      label: "Rejected",
      icon: XCircleIcon,
      solidIcon: XCircleIcon,
      colors: {
        default: "bg-red-100 text-red-800 border-red-200",
        solid: "bg-red-600 text-white",
        outline: "border-red-600 text-red-600",
        minimal: "text-red-600",
      },
    },
    pending: {
      label: "Pending",
      icon: ClockIcon,
      solidIcon: ClockIconSolid,
      colors: {
        default: "bg-orange-100 text-orange-800 border-orange-200",
        solid: "bg-orange-600 text-white",
        outline: "border-orange-600 text-orange-600",
        minimal: "text-orange-600",
      },
    },
    planning: {
      label: "Planning",
      icon: ClockIcon,
      solidIcon: ClockIconSolid,
      colors: {
        default: "bg-yellow-100 text-yellow-800 border-yellow-200",
        solid: "bg-yellow-600 text-white",
        outline: "border-yellow-600 text-yellow-600",
        minimal: "text-yellow-600",
      },
    },
    completed: {
      label: "Completed",
      icon: CheckCircleIcon,
      solidIcon: CheckCircleIconSolid,
      colors: {
        default: "bg-blue-100 text-blue-800 border-blue-200",
        solid: "bg-blue-600 text-white",
        outline: "border-blue-600 text-blue-600",
        minimal: "text-blue-600",
      },
    },
    "on-hold": {
      label: "On Hold",
      icon: ExclamationTriangleIcon,
      solidIcon: ExclamationTriangleIconSolid,
      colors: {
        default: "bg-orange-100 text-orange-800 border-orange-200",
        solid: "bg-orange-600 text-white",
        outline: "border-orange-600 text-orange-600",
        minimal: "text-orange-600",
      },
    },
    cancelled: {
      label: "Cancelled",
      icon: XCircleIcon,
      solidIcon: XCircleIcon,
      colors: {
        default: "bg-red-100 text-red-800 border-red-200",
        solid: "bg-red-600 text-white",
        outline: "border-red-600 text-red-600",
        minimal: "text-red-600",
      },
    },
    // Form validation states
    valid: {
      label: "Valid",
      icon: CheckCircleIcon,
      solidIcon: CheckCircleIconSolid,
      colors: {
        default: "bg-green-100 text-green-800 border-green-200",
        solid: "bg-green-600 text-white",
        outline: "border-green-600 text-green-600",
        minimal: "text-green-600",
      },
    },
    invalid: {
      label: "Invalid",
      icon: ExclamationCircleIcon,
      solidIcon: ExclamationCircleIconSolid,
      colors: {
        default: "bg-red-100 text-red-800 border-red-200",
        solid: "bg-red-600 text-white",
        outline: "border-red-600 text-red-600",
        minimal: "text-red-600",
      },
    },
    warning: {
      label: "Warning",
      icon: ExclamationTriangleIcon,
      solidIcon: ExclamationTriangleIconSolid,
      colors: {
        default: "bg-yellow-100 text-yellow-800 border-yellow-200",
        solid: "bg-yellow-600 text-white",
        outline: "border-yellow-600 text-yellow-600",
        minimal: "text-yellow-600",
      },
    },
    // Section completion states
    complete: {
      label: "Complete",
      icon: CheckCircleIcon,
      solidIcon: CheckCircleIconSolid,
      colors: {
        default: "bg-green-100 text-green-800 border-green-200",
        solid: "bg-green-600 text-white",
        outline: "border-green-600 text-green-600",
        minimal: "text-green-600",
      },
    },
    incomplete: {
      label: "Incomplete",
      icon: ClockIcon,
      solidIcon: ClockIconSolid,
      colors: {
        default: "bg-gray-100 text-gray-800 border-gray-200",
        solid: "bg-gray-600 text-white",
        outline: "border-gray-600 text-gray-600",
        minimal: "text-gray-600",
      },
    },
  };

  const config = statusConfig[status] || statusConfig.incomplete;
  const Icon = variant === "solid" ? config.solidIcon : config.icon;

  const sizeClasses = {
    xs: "px-1.5 py-0.5 text-xs",
    sm: "px-2 py-1 text-xs",
    md: "px-2.5 py-1.5 text-sm",
    lg: "px-3 py-2 text-base",
  };

  const iconSizes = {
    xs: "h-3 w-3",
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };

  const baseClasses = "inline-flex items-center font-medium rounded-full";
  const variantClasses =
    variant === "outline" ? "border" : variant === "minimal" ? "" : "";

  return (
    <span
      className={`${baseClasses} ${sizeClasses[size]} ${variantClasses} ${config.colors[variant]} ${className}`}
    >
      {showIcon && (
        <Icon
          className={`${iconSizes[size]} ${size === "xs" ? "mr-1" : "mr-1.5"}`}
        />
      )}
      {config.label}
    </span>
  );
}

/**
 * ErrorBadge Component
 *
 * Displays error count badges for form sections
 */
export function ErrorBadge({ errorCount, size = "sm", className = "" }) {
  if (!errorCount || errorCount === 0) return null;

  const sizeClasses = {
    xs: "px-1.5 py-0.5 text-xs",
    sm: "px-2 py-1 text-xs",
    md: "px-2.5 py-1.5 text-sm",
    lg: "px-3 py-2 text-base",
  };

  const iconSizes = {
    xs: "h-3 w-3",
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };

  return (
    <span
      className={`inline-flex items-center ${sizeClasses[size]} font-medium bg-red-100 text-red-800 rounded-full border border-red-200 ${className}`}
    >
      <ExclamationCircleIcon className={`${iconSizes[size]} mr-1`} />
      {errorCount} error{errorCount !== 1 ? "s" : ""}
    </span>
  );
}

/**
 * ProgressBadge Component
 *
 * Shows completion percentage with progress indicator
 */
export function ProgressBadge({
  completed,
  total,
  size = "sm",
  showPercentage = true,
  showIcon = true,
  className = "",
}) {
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  const getProgressColor = (pct) => {
    if (pct === 100) return "text-green-700 bg-green-100 border-green-200";
    if (pct >= 75) return "text-blue-700 bg-blue-100 border-blue-200";
    if (pct >= 50) return "text-yellow-700 bg-yellow-100 border-yellow-200";
    if (pct >= 25) return "text-orange-700 bg-orange-100 border-orange-200";
    return "text-gray-700 bg-gray-100 border-gray-200";
  };

  const sizeClasses = {
    xs: "px-1.5 py-0.5 text-xs",
    sm: "px-2 py-1 text-xs",
    md: "px-2.5 py-1.5 text-sm",
    lg: "px-3 py-2 text-base",
  };

  const iconSizes = {
    xs: "h-3 w-3",
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };

  return (
    <span
      className={`inline-flex items-center ${sizeClasses[size]} font-medium rounded-full border ${getProgressColor(percentage)} ${className}`}
    >
      {showIcon && <ChartBarIcon className={`${iconSizes[size]} mr-1`} />}
      {showPercentage ? `${percentage}%` : `${completed}/${total}`}
    </span>
  );
}

/**
 * FieldStatusIcon Component
 *
 * Small icon indicators for individual form fields
 */
export function FieldStatusIcon({ status, size = "sm", className = "" }) {
  const statusConfig = {
    valid: {
      icon: CheckCircleIcon,
      color: "text-green-500",
    },
    invalid: {
      icon: ExclamationCircleIcon,
      color: "text-red-500",
    },
    warning: {
      icon: ExclamationTriangleIcon,
      color: "text-yellow-500",
    },
    required: {
      icon: ExclamationCircleIcon,
      color: "text-gray-400",
    },
  };

  const config = statusConfig[status];
  if (!config) return null;

  const Icon = config.icon;

  const sizeClasses = {
    xs: "h-3 w-3",
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };

  return (
    <Icon className={`${sizeClasses[size]} ${config.color} ${className}`} />
  );
}

/**
 * SectionHeaderBadge Component
 *
 * Enhanced badges for form section headers
 */
export function SectionHeaderBadge({
  type,
  count,
  size = "sm",
  className = "",
}) {
  const badgeConfig = {
    fields: {
      icon: DocumentTextIcon,
      label: count === 1 ? "field" : "fields",
      color: "text-gray-600 bg-gray-100 border-gray-200",
    },
    errors: {
      icon: ExclamationCircleIcon,
      label: count === 1 ? "error" : "errors",
      color: "text-red-700 bg-red-100 border-red-200",
    },
    warnings: {
      icon: ExclamationTriangleIcon,
      label: count === 1 ? "warning" : "warnings",
      color: "text-yellow-700 bg-yellow-100 border-yellow-200",
    },
    completed: {
      icon: CheckCircleIcon,
      label: "complete",
      color: "text-green-700 bg-green-100 border-green-200",
    },
  };

  const config = badgeConfig[type];
  if (!config || count === 0) return null;

  const Icon = config.icon;

  const sizeClasses = {
    xs: "px-1.5 py-0.5 text-xs",
    sm: "px-2 py-1 text-xs",
    md: "px-2.5 py-1.5 text-sm",
    lg: "px-3 py-2 text-base",
  };

  const iconSizes = {
    xs: "h-3 w-3",
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };

  return (
    <span
      className={`inline-flex items-center ${sizeClasses[size]} font-medium rounded-full border ${config.color} ${className}`}
    >
      <Icon className={`${iconSizes[size]} mr-1`} />
      {count} {config.label}
    </span>
  );
}

/**
 * CategoryIcon Component
 *
 * Icons for different entity types and categories
 */
export function CategoryIcon({ category, size = "md", className = "" }) {
  const categoryConfig = {
    // Project types
    residential: { icon: BuildingOfficeIcon, color: "text-blue-600" },
    commercial: { icon: BuildingOfficeIcon, color: "text-purple-600" },
    "mixed-use": { icon: BuildingOfficeIcon, color: "text-indigo-600" },

    // Deal types
    development: { icon: SparklesIcon, color: "text-green-600" },
    acquisition: { icon: BanknotesIcon, color: "text-blue-600" },
    refinance: { icon: CurrencyPoundIcon, color: "text-yellow-600" },

    // Valuation types
    purchase: { icon: BanknotesIcon, color: "text-green-600" },
    refinance: { icon: CurrencyPoundIcon, color: "text-blue-600" },
    insurance: { icon: InformationCircleIcon, color: "text-orange-600" },

    // General categories
    financial: { icon: CurrencyPoundIcon, color: "text-green-600" },
    location: { icon: MapPinIcon, color: "text-red-600" },
    timeline: { icon: CalendarIcon, color: "text-blue-600" },
    contacts: { icon: UserGroupIcon, color: "text-purple-600" },

    // Default
    default: { icon: DocumentTextIcon, color: "text-gray-600" },
  };

  const config = categoryConfig[category] || categoryConfig.default;
  const Icon = config.icon;

  const sizeClasses = {
    xs: "h-3 w-3",
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
    xl: "h-8 w-8",
  };

  return (
    <Icon className={`${sizeClasses[size]} ${config.color} ${className}`} />
  );
}

/**
 * ActionIcon Component
 *
 * Icons for common actions with consistent styling
 */
export function ActionIcon({
  action,
  size = "sm",
  variant = "outline", // "outline", "solid"
  className = "",
}) {
  const actionConfig = {
    view: { icon: EyeIcon, color: "text-blue-600 hover:text-blue-800" },
    edit: { icon: PencilIcon, color: "text-gray-600 hover:text-gray-800" },
    complete: {
      icon: CheckCircleIcon,
      color: "text-green-600 hover:text-green-800",
    },
    delete: { icon: XCircleIcon, color: "text-red-600 hover:text-red-800" },
  };

  const config = actionConfig[action];
  if (!config) return null;

  const Icon = config.icon;

  const sizeClasses = {
    xs: "h-3 w-3",
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };

  return (
    <Icon
      className={`${sizeClasses[size]} ${config.color} transition-colors ${className}`}
    />
  );
}

export default {
  StatusBadge,
  ErrorBadge,
  ProgressBadge,
  FieldStatusIcon,
  SectionHeaderBadge,
  CategoryIcon,
  ActionIcon,
};
